/* =========================================================================
 * CORRECT THIS FIELD — the pure model. PURE, ZERO IMPORTS.
 *
 * Brian: "there needs to be a manual push that any of my team can do." The
 * Command Center has been able to FIND a bad record for months — the gap flags,
 * the issue chips, the no-source warning all work — and then the fix lived
 * somewhere else: a terminal run, a privileged tool, or nowhere. Every real
 * correction routed back to Brian, which is the thing he is trying to stop
 * before his team takes this over.
 *
 * ⚠️ THE FIELD LIST IS THE SERVER'S, NEVER OURS. Everything here is driven off
 * `GET /api/champion-fields`. That route has reported tiers, the caller's own
 * `mayWrite` list, a `never` list and the lock's protected set since wave 3
 * probed it, and NOTHING in this app was reading it. Hardcoding a list instead
 * would drift from the server the first time a tier changed — and the drift
 * would be invisible, because a client list and a server list disagree silently
 * until somebody presses the button.
 *
 * ⚠️ AND THE DOC LINE THIS BUILD STARTED FROM WAS STALE. CLAUDE.md describes a
 * four-field allowlist (`current_champion`, `sr_pursuit_status`,
 * `sr_confirmed_date`, `sr_assigned_rep`). Measured live 2026-09-14, the
 * OPERATIONAL tier alone is 23 fields and the PRIVILEGED tier is another 14,
 * including `championship_name`, `sr_event_date`, `sr_sport`, `sr_state` and
 * `champion_locked`. The corrections this build needs were never absent from
 * the API — they were absent from the UI, and gated behind a grant. Reading the
 * route is the fix; believing the doc is what made it look impossible.
 *
 * ⚠️ PURE, WITH ZERO IMPORTS, AND THAT IS LOAD-BEARING. The network half is
 * `fieldEditRun.js`. `lib/api.js` reads `import.meta.env`, so anything importing
 * it cannot be driven under plain Node — and every rule in this file is a rule
 * `verify-hunt.mjs` has to EXECUTE rather than regex for. Same split, and the
 * same reason, as `clearCrown.js` beside `clearCrownRun.js`.
 * ========================================================================= */

const S = (v) => String(v ?? '').trim()

/* ---- THE REASON FLOOR ---------------------------------------------------
 * A privileged write is `override: true` plus a reason, and the reason goes to
 * the ledger. A one-word reason satisfies a `required` check and explains
 * nothing six months later — and "an edit nobody can explain later" is exactly
 * how this object ended up with a champion's name inside its own title. So
 * there is a floor, and it is stated to the operator before they type.
 */
export const REASON_MIN = 12

/* ---- WHAT A CORRECTION IS FOR ------------------------------------------
 *
 * ⚠️ THIS IS A DISPLAY SPEC, NOT AN ALLOWLIST, AND THE DIFFERENCE IS THE WHOLE
 * DESIGN. Permission comes from the policy; this only says how a field is
 * LABELLED and EDITED, and which fields this particular control is about.
 *
 * The policy's 37 writable fields include machine provenance —
 * `champion_run_id`, `champion_detected_at`, `champion_search_attempts` — which
 * a person has no business typing into a drawer. Drawing all 37 would bury the
 * five that matter. So the surfaced set is the record's IDENTITY: the fields the
 * drawer's record grid already shows a person, plus the lock.
 *
 * Every entry is asserted to exist in the live policy (`specDrift`), so a
 * server-side rename shows up as a loud drift report rather than as a field that
 * quietly stops being editable.
 */
export const FIELD_SPECS = {
  // Display specifications only. CTC opts in explicitly; the board's SURFACED
  // list and every caller's permissions remain unchanged.
  champion_head_coach: { label: 'Historical head coach', kind: 'text' },
  champion_story: { label: 'Season story', kind: 'textarea' },
  champion_story_source: { label: 'Story evidence URL', kind: 'url' },
  championship_name: {
    label: 'Championship name',
    recordKey: 'name',
    kind: 'text',
    help: 'The name a rep reads and the name that reaches an engraving. A champion, a score or a '
      + 'note that leaked into this field is not a cosmetic problem — it is what gets engraved.',
  },
  sr_sport: { label: 'Sport', recordKey: 'sport', kind: 'text' },
  sr_state: {
    label: 'State',
    recordKey: 'state',
    kind: 'text',
    help: 'Two-letter USPS, or blank for a title with no single state. The board reads this first '
      + 'and falls back to the location, so a wrong code moves the record on the map.',
  },
  sr_level: { label: 'Level', recordKey: 'level', kind: 'text' },
  sr_conference: { label: 'Conference / governing body', recordKey: 'conference', kind: 'text' },
  sr_season_year: {
    label: 'Season start year', recordKey: 'season', kind: 'text',
    help: 'The start year of the July–June season. A spring 2027 championship belongs to 2026–2027 and stores 2026. Check the title and official season before changing it.',
  },
  sr_segment: {
    label: 'Division',
    recordKey: 'segment',
    kind: 'text',
    /* The PSAL lesson, stated where somebody is about to type into it. */
    help: 'An ENUM. HubSpot rejects a value that is not on the list and the whole write is lost — '
      + '68 PSAL records failed exactly that way on "HS City".',
    enumish: true,
  },
  sr_event_date: {
    label: 'Event date',
    recordKey: 'effDate',
    kind: 'date',
    help: 'The date the hunt fires on. YYYY-MM-DD.',
  },
  sr_date_status: {
    label: 'Date status',
    recordKey: 'dateStatus',
    kind: 'text',
    help: 'Confirmed means somebody checked it against the governing body — not that somebody typed it.',
  },
  sr_date_source: {
    label: 'Date source',
    kind: 'text',
    help: 'Record the official calendar URL, event and verified date. A source URL in the change reason does not replace this field. Replace projection notes only after checking the official source. Reload the record after saving to refresh reconciliation.',
  },
  champion_locked: {
    label: 'Champion lock',
    recordKey: 'championLocked',
    kind: 'boolean',
    /* ⚠️ THE ONE FIELD THAT CAN ONLY EVER BE SET FROM A HUMAN UI, and on its own
     * it justifies this build. The server answers `automation_forbidden` to a
     * service identity — measured, not assumed — so no engine, no grant and no
     * scheduled job can set it. Before this control the only way it got set was
     * as a SIDE EFFECT of recording a champion by hand, which meant a record
     * with no champion could never be locked at all. Both CIF phantoms are
     * exactly that record. */
    help: 'A human-only override. Once locked, automation cannot change the champion or its '
      + 'provenance, and the crown-withdrawal control refuses the record outright.',
    humanOnly: true,
  },
}

/** The order they are drawn in — identity first, the lock last, because the lock
 *  is a decision about the record rather than a value on it. */
export const SURFACED = [
  'championship_name', 'sr_sport', 'sr_state', 'sr_level',
  'sr_conference', 'sr_segment', 'sr_event_date', 'sr_date_status', 'sr_date_source',
  'champion_locked',
]

/* ---- READING THE POLICY ------------------------------------------------
 *
 * ⚠️ `?id=` IS IGNORED BY THE ROUTE — measured. The response is byte-identical
 * for every record, so this is a POLICY report about the CALLER, not a report
 * about a record. Per-record state (the current value, whether the lock is on)
 * comes from the record itself. Treating this as per-record would have produced
 * a control that looked record-aware and was not.
 */
export function readFieldPolicy(res) {
  const r = res || {}
  const tiers = r.tiers || {}
  const list = (v) => (Array.isArray(v) ? v.map(S).filter(Boolean) : [])
  const caller = r.caller || {}

  return {
    ok: r.ok === true,
    object: S(r.object),
    operational: list(tiers.operational?.fields),
    privileged: list(tiers.privileged?.fields),
    operationalRequires: S(tiers.operational?.requires),
    privilegedRequires: S(tiers.privileged?.requires),
    never: list(r.never),
    lockField: S(r.lock?.field),
    lockProtects: list(r.lock?.protects),
    lockOverride: S(r.lock?.override),
    /* ⚠️ `mayWrite` IS THE AUTHORITATIVE PER-CALLER ANSWER and it is read in
     * preference to (tier + canRepairData). The tier lists say what a field IS;
     * only this says what THIS session may do. Re-deriving it from the flags
     * would be a second copy of the server's rule, and the two would disagree
     * the day the server grew a third tier. */
    mayWrite: list(caller.mayWrite),
    userId: S(caller.userId),
    role: S(caller.role),
    canRepairData: caller.canRepairData === true,
  }
}

/** The tier a field sits in. `never` and `unknown` are told apart: one is a
 *  server decision, the other means we are asking about a field the server has
 *  not heard of — usually a rename, and never something to draw an input for. */
export function tierOfField(policy, field) {
  const f = S(field)
  const p = policy || {}
  if ((p.never || []).includes(f)) return 'never'
  if ((p.operational || []).includes(f)) return 'operational'
  if ((p.privileged || []).includes(f)) return 'privileged'
  return 'unknown'
}

/* ---- MAY THIS CALLER EDIT THIS FIELD, AND IF NOT, WHY -------------------
 *
 * Three outcomes, not two, and the third is the one that matters:
 *
 *   drawable: false  — there is no question to answer. A `never` field and an
 *                      unrecognised field get NO control at all, not a disabled
 *                      one. `sr_champion_status` is guarded by a backend trigger
 *                      and `sr_championship_key` is the registry key the dedupe
 *                      runs on; offering either, even greyed out, invites
 *                      somebody to ask for the permission.
 *   editable: false  — the field is real and correctable, this session just may
 *                      not. Shown, disabled, WITH the reason — the OutreachPicker
 *                      and ClearCrown rule. A rep who sees no control concludes
 *                      the feature is broken and asks for it to be fixed.
 *   editable: true   — draw the input.
 *
 * ⚠️ THIS IS A DRAWING DECISION ONLY (hard rule 5). `champion-update` re-decides
 * every clause of it against the verified JWT and answers 403
 * `data_repair_locked` / `automation_forbidden`. Nothing here secures anything,
 * and the refusal is displayed when the two disagree.
 */
export function mayEditField(policy, field) {
  const p = policy || {}
  const f = S(field)
  const tier = tierOfField(p, f)

  if (!p.ok) {
    return {
      drawable: false, editable: false, tier,
      why: 'The field policy has not loaded, so nothing here knows what may be edited.',
    }
  }

  if (tier === 'never') {
    return {
      drawable: false, editable: false, tier,
      why: `The server lists ${f} as never writable through this route.`,
    }
  }

  if (tier === 'unknown') {
    return {
      drawable: false, editable: false, tier,
      why: `${f} is not in any tier the field policy reports. It may have been renamed server-side.`,
    }
  }

  if (!(p.mayWrite || []).includes(f)) {
    return {
      drawable: true, editable: false, tier,
      why: tier === 'privileged'
        ? `Correcting ${f} requires ${p.privilegedRequires || 'exec or the Data Repair grant'}. `
          + `This session is ${p.role || 'unrecognised'}${p.canRepairData ? '' : ' without that grant'}.`
        : `This session may not write ${f}.`,
    }
  }

  return { drawable: true, editable: true, tier, why: '' }
}

/** Every surfaced field, resolved against the policy and the record. One pass,
 *  so the component never re-derives a rule. */
export function editableFields(policy, record, { order = SURFACED } = {}) {
  return order
    .map((field) => {
      const spec = FIELD_SPECS[field]
      if (!spec) return null
      const gate = mayEditField(policy, field)
      if (!gate.drawable) return null
      return {
        field,
        spec,
        ...gate,
        current: currentValue(record, field),
        lockProtected: (policy?.lockProtects || []).includes(field),
      }
    })
    .filter(Boolean)
}

/** ⚠️ A SPEC THAT NAMES A FIELD THE SERVER DOES NOT IS A BUG TO REPORT, NOT TO
 *  SWALLOW. Returns the surfaced fields absent from every tier — i.e. the ones
 *  a server rename has just broken. `unknown` already stops them being drawn;
 *  this is how anybody finds out. */
export function specDrift(policy, { order = SURFACED } = {}) {
  if (!policy?.ok) return []
  return order.filter((f) => tierOfField(policy, f) === 'unknown')
}

/* ---- THE CURRENT VALUE, AS THE RECORD ACTUALLY HOLDS IT -----------------
 *
 * ⚠️ SHOW WHAT IT IS BEFORE ASKING WHAT IT SHOULD BE. A blind edit box on a
 * privileged field is how `sr_state: MA` lands on a California record — which is
 * not hypothetical: it is the live state of 57419861308 right now.
 *
 * ⚠️ AND IT READS THE **RAW HUBSPOT PROPERTY**, NOT THE NORMALIZED VALUE, ON
 * EVERY FIELD. This started as a special case for the date and that was not
 * enough — proving the editor against the four real records showed the same trap
 * on three more fields. `normalizeChampionship` TRANSFORMS on the way past:
 *
 *   sr_state        "Georgia" → `state: "GA"`      (normalizeState)
 *   sr_conference   → `conference`                  (cleanConference)
 *   sr_date_status  absent     → "Confirmed" / "Projected" / "TBD" (invented)
 *   sr_event_date   → `effDate` = confirmed ?? event ?? projected
 *
 * Every one of those would show the operator one value and change another. On
 * record 57426948379 the grid says GA and the property says "Georgia": a
 * no-change check against the normalized value would have called a real edit a
 * no-op, and the re-read — which compares against the raw property — would have
 * reported a good write as a mismatch. The field being edited IS the raw
 * property, so the raw property is what is shown, compared and confirmed.
 *
 * The normalized key is a FALLBACK only, for a record whose `raw` the caller did
 * not carry (`?demo=1`, or a test fixture).
 */
export function currentValue(record, field) {
  const c = record || {}
  const f = S(field)
  const spec = FIELD_SPECS[f]
  if (!spec) return ''

  /* ⚠️ THE FALLBACK IS PER-RECORD, NOT PER-KEY, AND THAT DISTINCTION IS A REAL
   * BUG THIS CAUGHT. Falling back whenever the raw object lacked the KEY looked
   * equivalent and was not: `sr_date_status` is INVENTED by the normalizer when
   * the property is absent ("Confirmed" / "Projected" / "TBD"), so a record
   * that genuinely holds no date status would have shown the operator "TBD" as
   * its current value — a word the record has never held — and then refused
   * their edit to "TBD" as a no-change.
   *
   * So: if the record carries a `raw` object at all, the raw object is the
   * truth and a missing key means the property is EMPTY. The normalized key is
   * the fallback only for a record with no `raw` whatsoever, which is the
   * `?demo=1` and test-fixture case. */
  const hasRawObject = c.raw && typeof c.raw === 'object'
  const v = hasRawObject ? c.raw[f] : c[spec.recordKey]

  if (spec.kind === 'boolean') {
    /* Mirrors `isChampionLocked` — including its direction: an unrecognised
     * value reads as LOCKED, because a client reading looser than the server
     * draws a live control over a record the server vetoes. */
    if (v == null || v === '') return false
    return !['false', '0', 'no', 'off', 'null', 'undefined'].includes(String(v).trim().toLowerCase())
  }

  /* A date property is stored as an ISO string or an epoch stamp; the input is
   * `type="date"` and needs YYYY-MM-DD. */
  if (spec.kind === 'date') return v ? String(v).slice(0, 10) : ''

  return v == null ? '' : String(v)
}

/** What the record will read afterwards, in the operator's own terms. Used for
 *  the before → after line, which is the whole defence against a blind edit. */
export function renderValue(field, value) {
  const spec = FIELD_SPECS[S(field)]
  if (spec?.kind === 'boolean') return value ? 'Locked' : 'Not locked'
  const s = S(value)
  return s || '(blank)'
}

/* ---- IS THIS EDIT EVEN A CHANGE, AND IS IT SAYABLE ----------------------
 *
 * Returns the blocker, or null. Ordered so the operator hears the most useful
 * objection first.
 */
export function editProblem({ field, current, next, reason, gate }) {
  const spec = FIELD_SPECS[S(field)]
  if (!spec) return { kind: 'unknown-field', message: `${field} is not a correctable field.` }
  if (!gate?.editable) return { kind: 'denied', message: gate?.why || 'Not permitted.' }

  const isBool = spec.kind === 'boolean'
  const a = isBool ? Boolean(current) : S(current)
  const b = isBool ? Boolean(next) : S(next)

  /* ⚠️ A NO-CHANGE WRITE IS REFUSED HERE RATHER THAN SENT. The route accepts it
   * and answers `ok: true` with the field in `fields` — a real ledger row for a
   * value nobody changed. That pollutes the one trail somebody will read when
   * they need to know who changed a name and why. */
  if (a === b) {
    return {
      kind: 'nochange',
      message: `That is what the record already says. Nothing to correct.`,
    }
  }

  /* ⚠️ BLANKING IS A REAL CORRECTION AND IT IS NOT A MISTAKE TO PREVENT —
   * clearing `sr_state` on a multi-state association is the RIGHT answer (the
   * MAIS finding). But `champion-update` drops a blank into `ignored` and
   * answers ok having changed nothing, so a blank sent naively is the silent
   * no-op this build exists to stop. It is declared instead. */
  if (!isBool && !b) {
    return {
      kind: 'blanking',
      declare: true,
      message: 'Clearing a field is a real correction, and the route drops an undeclared blank '
        + 'without writing it — so it has to be said explicitly.',
    }
  }

  const why = S(reason)
  if (!why) {
    return {
      kind: 'noreason',
      message: 'A reason is required. It goes to the ledger, and it is the only thing that will '
        + 'explain this change to whoever reads the record next.',
    }
  }
  if (why.length < REASON_MIN) {
    return {
      kind: 'thinreason',
      message: `That reason is ${why.length} characters. Say what was wrong and how this value was `
        + `established — at least ${REASON_MIN} characters.`,
    }
  }

  if (spec.kind === 'date' && b && !/^\d{4}-\d{2}-\d{2}$/.test(b)) {
    return { kind: 'baddate', message: 'Use YYYY-MM-DD.' }
  }
  if (field === 'sr_season_year' && !/^(?:19|20|21)\d{2}$/.test(b)) {
    return { kind: 'badseason', message: 'Use the four-digit season start year, such as 2026 for 2026–2027.' }
  }

  return null
}

/* ---- THE REQUEST -------------------------------------------------------
 *
 * ⚠️ ONE FIELD PER CALL, DELIBERATELY. The route takes several at once, and a
 * multi-field write is where a refusal becomes unreadable: `data_repair_locked`
 * names the fields it refused, so a three-field write that half-lands leaves the
 * operator reading a partial success against three before/after pairs. One field
 * per call means every verdict is about the thing on screen.
 *
 * `override: true` is sent for a privileged field because the route's own lock
 * documentation requires it ("send `override: true` with a `reason`"). It is
 * NOT sent for an operational field — an override flag on a write that needs no
 * override is a habit that makes the flag meaningless.
 */
export function buildEditRequest({ id, field, value, reason, gate, lockProtected = false, allowClear = false }) {
  const spec = FIELD_SPECS[S(field)]
  const privileged = gate?.tier === 'privileged'
  const body = {
    id: String(id),
    reason: S(reason),
  }
  body[field] = spec?.kind === 'boolean' ? Boolean(value) : S(value)
  if (allowClear === true && !S(value) && spec?.kind !== 'boolean') body.allowClear = [field]
  if (privileged || lockProtected) body.override = true
  return body
}

/* ---- READING THE VERDICT ----------------------------------------------
 *
 * ⚠️ SUCCESS AND REFUSAL DO NOT USE THE SAME KEY, AND THAT IS THE TRAP.
 * Measured live 2026-09-14 against the real route:
 *
 *   CONFIRMED  200  { ok: true,  id, by, fields: ['sr_pursuit_status'], tier }
 *   REFUSED    400  { ok: false, error: 'hubspot_update_failed', wrote: [],
 *                     detail: { error: 'no_updatable_fields', rejected, ignored, allowed, tier } }
 *   REFUSED    403  { ok: false, error: 'data_repair_locked',  wrote: [], fields: [...], logged }
 *   REFUSED    403  { ok: false, error: 'automation_forbidden', wrote: [], fields: [...], logged }
 *
 * There is NO `wrote` key on a success and NO `fields`-means-written on a 403 —
 * on a refusal, `fields` is the list it REFUSED. So neither key alone is safe:
 * reading `wrote` reports every real write as nothing, and reading `fields`
 * reports every 403 as written. The only affirmative is `ok === true` AND the
 * field named in `fields`, and that conjunction is asserted.
 *
 * `champion-update` has answered `ok: true` having changed nothing (a blank
 * dropped into `ignored`), which is why the field must be NAMED and not merely
 * implied by a 200.
 */
export function readEditResult(res, field) {
  const r = res || {}
  const f = S(field)
  const named = (Array.isArray(r.fields) ? r.fields : []).map(S)

  /* THE ONLY YES. Both halves required. */
  if (r.ok === true && named.includes(f)) {
    return {
      kind: 'written',
      field: f,
      tier: S(r.tier),
      by: S(r.by),
      message: 'The correction is written, and the reason is in the ledger.',
    }
  }

  /* A 200 that did not name the field. The route accepted the call and changed
   * nothing — an ignored blank, or a field it silently dropped. Reported as the
   * no-op it is, never as a success. */
  if (r.ok === true) {
    return {
      kind: 'nothing-written',
      message: named.length
        ? `The server reports it wrote ${named.join(', ')} — not ${f}.`
        : `The server accepted the call and named no field it wrote.`,
      hint: 'Nothing was changed on this field. Reload the record before trying again.',
    }
  }

  const err = S(r.error)
  const detail = r.detail || {}

  if (err === 'automation_forbidden') {
    return {
      kind: 'refused',
      message: S(r.reason) || 'A service identity may never write this field.',
      hint: r.logged?.seq ? `The refusal is in the ledger (seq ${r.logged.seq}). Nothing was written.` : 'Nothing was written.',
    }
  }

  if (err === 'data_repair_locked') {
    return {
      kind: 'denied',
      message: S(r.reason) || 'Structural fields are locked without the Data Repair grant.',
      hint: r.logged?.seq
        ? `The refusal is in the ledger (seq ${r.logged.seq}). Nothing was written, and this is an `
          + `exec's grant to set — not something to route around.`
        : 'Nothing was written.',
    }
  }

  if (S(detail.error) === 'no_updatable_fields') {
    const rejected = (Array.isArray(detail.rejected) ? detail.rejected : []).map(S)
    return {
      kind: 'refused',
      message: rejected.includes(f)
        ? `The server does not accept ${f} at the ${S(detail.tier) || 'current'} tier.`
        : S(detail.reason) || 'Nothing was written.',
      hint: 'Nothing was written.',
    }
  }

  return {
    kind: 'refused',
    message: S(r.reason) || S(detail.reason) || err || 'The correction did not run.',
    hint: 'Nothing was written.',
  }
}

/* ---- THE RE-READ -------------------------------------------------------
 *
 * ⚠️ A CONFIRMED WRITE IS STILL NOT PROOF THE RECORD READS THE NEW VALUE, and
 * `sr_segment` is why: it is an ENUM, and HubSpot rejects a value that is not on
 * its list. 68 PSAL records were lost exactly that way. So the record is read
 * back and the value compared, and a mismatch is its own outcome rather than
 * being folded into the success.
 *
 * ⚠️ THE RE-READ MUST NOT BE THE BOARD'S SNAPSHOT. `/api/championships/all` is
 * stale-while-revalidate — it hands back what it already has and rebuilds
 * behind you, even with `?force=1` — so confirming a write against it would
 * report a good write as a mismatch and a bad one as fine, depending only on
 * timing. `GET /api/championship/:id` is the direct read (38 raw properties, no
 * `asOf`), and it is the only thing this compares against.
 *
 * It returns RAW HubSpot property names, which is what is compared — putting the
 * normalizer between the write and its confirmation would mean comparing a
 * transformed value against an untransformed one.
 */
export function confirmWrite({ field, expected, afterRaw }) {
  const f = S(field)
  const spec = FIELD_SPECS[f]

  if (!afterRaw) {
    return {
      state: 'unverified',
      message: 'The server confirmed the write, but the record could not be read back — so what it '
        + 'reads now is not known here.',
      hint: 'Reopen the record to see its current state.',
    }
  }

  const raw = afterRaw[f]
  const live = spec?.kind === 'boolean'
    ? !(raw == null || raw === '' || ['false', '0', 'no', 'off', 'null', 'undefined'].includes(String(raw).trim().toLowerCase()))
    : (raw == null ? '' : String(raw).slice(0, spec?.kind === 'date' ? 10 : undefined))

  const same = spec?.kind === 'boolean'
    ? Boolean(live) === Boolean(expected)
    : S(live) === S(expected)

  if (same) {
    return { state: 'confirmed', live, message: `The record now reads ${renderValue(f, live)}.` }
  }

  return {
    state: 'mismatch',
    live,
    message: `⚠️ The server confirmed the write, but reading the record back gives `
      + `${renderValue(f, live)} — not ${renderValue(f, expected)}. ${spec?.enumish
        ? 'This field is an enum: HubSpot rejects a value that is not on its list, and it does so '
          + 'after the route has already accepted the call.'
        : 'The value was transformed on the way in, or something changed it back.'}`,
    hint: 'Treat this field as NOT corrected.',
  }
}

/* ---- WHO CAN BE ASKED --------------------------------------------------
 *
 * A pursuit-adapted row (an RFP, a police department, a corporate account) has
 * no record in 2-64425303 at all, so there is no field to correct.
 *
 * ⚠️ AND IT DELIBERATELY DOES **NOT** EXCLUDE A RETIRED RECORD, WHICH IS WHERE
 * IT DIVERGES FROM `canClearCrown` — that divergence is the point, not an
 * oversight.
 *
 * `normalizeChampionship` derives `retired` by testing the NAME against a
 * leading "RETIRED -" prefix.
 * So retiring a phantom by renaming it to "RETIRED - …" makes the record
 * retired the instant the rename lands. If this test excluded retired records,
 * the editor would vanish from under the operator the moment they used it — and
 * the intended sequence on both CIF phantoms is rename AND THEN LOCK. The lock
 * would have become unreachable by the very act of retiring the record, which
 * is the one case it is most needed: a retired phantom with no champion is
 * exactly what the next sweep would re-crown.
 *
 * A retired record is still a record whose fields can be wrong, and a
 * mis-retirement is itself a correction somebody has to be able to make.
 * `canClearCrown` excludes retired because there is no crown on a retired
 * phantom to withdraw; that reasoning does not transfer.
 */
export function canEditFields(record) {
  const c = record || {}
  return Boolean(c.id) && !c.sourceType
}

/** The audit `before` pair. The old value is the single thing most worth having
 *  in the trail — after the write nothing on the record holds it. */
export function editBefore(record, field) {
  return {
    field: S(field),
    was: currentValue(record, field),
    championship: S(record?.name),
  }
}

/* ⚠️ THE FIELDS WHOSE NORMALIZED VALUE IS NOT THEIR STORED VALUE.
 *
 * `normalizeChampionship` transforms each of these, so the raw value written is
 * NOT what the board should display:
 *
 *   sr_state        `normalizeState` maps "Georgia" → "GA", and blank/USA/Canada
 *                   → "NATIONAL"
 *   sr_conference   `cleanConference` rewrites it
 *   sr_date_status  invented when absent ("Confirmed" / "Projected" / "TBD")
 *   sr_event_date   collapsed into `effDate` = confirmed ?? event ?? projected,
 *                   so a corrected event date may not change the effective one
 *                   at all — confirmed still wins
 *
 * Re-implementing any of those here would be a second copy of the normalizer,
 * and the copy would drift. So these get NO optimistic patch and the next real
 * load carries them. A row that is briefly stale is recoverable; a row showing
 * a value the record does not hold is the two-sources-of-truth fault this board
 * has been bitten by before.
 */
const DERIVED_FIELDS = ['sr_state', 'sr_conference', 'sr_date_status', 'sr_event_date', 'sr_season_year']

/** The optimistic patch, in NORMALIZED keys, composed only from the field the
 *  server NAMED and the re-read confirmed. Patching a raw HubSpot key into a
 *  store of normalized records is a live bug elsewhere on this page (`onHunted`
 *  in CommandCenter.jsx) and it is not being repeated here. Returns null rather
 *  than guessing. */
export function editPatch(field, value) {
  const f = S(field)
  const spec = FIELD_SPECS[f]
  if (!spec?.recordKey) return null
  if (DERIVED_FIELDS.includes(f)) return null
  const patch = {}
  patch[spec.recordKey] = spec.kind === 'boolean' ? Boolean(value) : S(value)
  return patch
}

/** Whether the board's displayed value for this field is DERIVED rather than
 *  stored — so the UI can say so instead of leaving the operator to wonder why
 *  the row above reads differently from the stored value. */
export function isDerivedField(field) {
  return DERIVED_FIELDS.includes(S(field))
}
